import { createFileRoute } from "@tanstack/react-router";
import { assertIsNotNull, assertIsNotUndefined } from "common/utils/assertOfType";
import { OS, getOS } from "common/utils/context";
import { cameraToTHREE, mesh3ToTHREE, scaleBufferGeometryUVs } from "gfx/convert";
import { EnvPreset } from "gfx/EnvPreset";
import { initPhysicallyBasedRenderer, initTHREEJSRenderer } from "gfx/initPhysicallyBasedRenderer";
import { initWebGLContext } from "gfx/initWebGLContext";
import { designMaterialNew } from "gfx/materials/designMaterial";
import { defaultDesignMaterialLoadOptions } from "gfx/materials/designMaterialRuntimeConfig";
import { cloneDefaultMaterialParams } from "gfx/materials/designMaterialUtils";
import { noIdMaterial } from "gfx/materials/idMaterial";
import { loadAllMaterialEditorTextures } from "gfx/materials/materialEditorTextures";
import { envPresetSceneLights } from "gfx/sceneLights";
import { RenderParamsSource } from "gfx/threejs/higharc/RenderParamsSource";
import type { RenderTargetPool } from "gfx/threejs/higharc/RenderTargetPool";
import { SSFXRendererOutput } from "gfx/threejs/higharc/SSFXRendererOutput";
import type { OnScreenRendererParamOverrides, SSFXRendererParamsManager } from "gfx/threejs/higharc/SSFXRendererParams";
import SSFXRendererScene from "gfx/threejs/higharc/SSFXRendererScene";
import type { SSFXRenderTarget } from "gfx/threejs/higharc/SSFXRenderTarget";
import { WebGLExtendedCapabilities } from "gfx/WebGLExtendedCapabilities";
import GraphicsTroubleshootingDocs, {
  GraphicsTroubleshootingSectionKey,
} from "lib/components/debug/GraphicsTroubleshootingDocs";
import getPageTitle from "lib/utils/getPageTitle";
import type { MutableRefObject, JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import { testCubeMesh } from "test/util/geometryGenerator";
import * as THREE from "three";
import { TextWithHighlights } from "ui/text/TextWithHighlights";

interface GfxFeature {
  name: string;
  enable: (
    renderer: THREE.WebGLRenderer,
    scene: SSFXRendererScene,
    renderParams: OnScreenRendererParamOverrides,
    fetchedTexturesRef: MutableRefObject<{ [name: string]: THREE.Texture } | null>,
  ) => Promise<void>;
}

const cameraSettings = {
  name: "Default",
  fov: 20,
  ortho: false,
  x: 20,
  y: 12,
  z: 40,
  targetX: 0,
  targetY: 0,
  targetZ: 0,
  upX: 0,
  upY: 0,
  upZ: 1,
  cameraNear: 5,
  cameraFar: 100,
} as const;

const viewerAspect = 1;
const camera = cameraToTHREE(cameraSettings, viewerAspect);

function getAllMeshes(scene: SSFXRendererScene): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];
  scene.scene.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      meshes.push(obj);
    }
  });
  return meshes;
}

const features: GfxFeature[] = [
  {
    name: "Scene",
    enable: async (
      _renderer: THREE.WebGLRenderer,
      scene: SSFXRendererScene,
      _renderParams: OnScreenRendererParamOverrides,
      _fetchedTexturesRef: MutableRefObject<{ [name: string]: THREE.Texture } | null>,
    ) => {
      const material = new THREE.MeshStandardMaterial({ color: "#55ff33" });
      material.userData.alternativeMaterial = noIdMaterial;
      const TEST_MESH_ID = 1;
      const cubeMesh3 = testCubeMesh(2, TEST_MESH_ID);
      const cube = mesh3ToTHREE(cubeMesh3, material);
      scene.scene.add(cube);

      const planeGeom = new THREE.PlaneGeometry(20, 20);
      scaleBufferGeometryUVs(planeGeom, 20.0);
      const plane = new THREE.Mesh(planeGeom, material);
      plane.position.set(0, 0, -2);
      plane.updateMatrixWorld();
      scene.scene.add(plane);

      const sceneLights = {
        ...envPresetSceneLights[EnvPreset.WHITEBOX],
        interiorAmbientLight: undefined, // disable the interior ambient light
      };
      await scene.sceneLightsManager.renderSceneLights(sceneLights, {});
      scene.scene.add(scene.sceneLightsManager.group);

      scene.scene.add(camera);
    },
  },
  {
    name: "AO",
    enable: async (
      _renderer: THREE.WebGLRenderer,
      _scene: SSFXRendererScene,
      renderParams: OnScreenRendererParamOverrides,
      _fetchedTexturesRef: MutableRefObject<{ [name: string]: THREE.Texture } | null>,
    ) => {
      renderParams.renderPipelineParams = { output: SSFXRendererOutput.AO };
    },
  },
  {
    name: "SSAA",
    enable: async (
      _renderer: THREE.WebGLRenderer,
      _scene: SSFXRendererScene,
      renderParams: OnScreenRendererParamOverrides,
      _fetchedTexturesRef: MutableRefObject<{ [name: string]: THREE.Texture } | null>,
    ) => {
      renderParams.screenRenderParams = { ssaaEnabled: true, reprojectionTAAEnabled: false };
    },
  },
  {
    name: "SSAA #2",
    enable: async (
      _renderer: THREE.WebGLRenderer,
      _scene: SSFXRendererScene,
      renderParams: OnScreenRendererParamOverrides,
      _fetchedTexturesRef: MutableRefObject<{ [name: string]: THREE.Texture } | null>,
    ) => {
      renderParams.screenRenderParams = { ssaaEnabled: true, reprojectionTAAEnabled: true };
    },
  },
  {
    name: "Lights",
    enable: async (
      _renderer: THREE.WebGLRenderer,
      _scene: SSFXRendererScene,
      renderParams: OnScreenRendererParamOverrides,
      _fetchedTexturesRef: MutableRefObject<{ [name: string]: THREE.Texture } | null>,
    ) => {
      renderParams.renderPipelineParams = { output: SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS };
    },
  },
  {
    name: "Material #1",
    enable: async (
      renderer: THREE.WebGLRenderer,
      scene: SSFXRendererScene,
      _renderParams: OnScreenRendererParamOverrides,
      fetchedTexturesRef: MutableRefObject<{ [name: string]: THREE.Texture } | null>,
    ) => {
      fetchedTexturesRef.current = await loadAllMaterialEditorTextures();
      const materialParams = cloneDefaultMaterialParams();
      materialParams.color = "#55ff33";
      const loadOptions = {
        ...defaultDesignMaterialLoadOptions,
        disableSDF: true,
        disableLayers: true,
        useWebgl2TextureAtlas: renderer.capabilities.isWebGL2,
      };
      const material = await designMaterialNew(
        materialParams,
        renderer,
        (url, _overrideFormat) => {
          assertIsNotNull(fetchedTexturesRef.current);
          return [fetchedTexturesRef.current[url], Promise.resolve()];
        },
        true,
        undefined,
        loadOptions,
      );
      const meshes = getAllMeshes(scene);
      for (const mesh of meshes) {
        mesh.material = material;
      }
    },
  },
  {
    name: "Material #2",
    enable: async (
      renderer: THREE.WebGLRenderer,
      scene: SSFXRendererScene,
      _renderParams: OnScreenRendererParamOverrides,
      fetchedTexturesRef: MutableRefObject<{ [name: string]: THREE.Texture } | null>,
    ) => {
      const materialParams = cloneDefaultMaterialParams();
      materialParams.color = "#55ff33";
      materialParams.sdf.common.maskOnly = false;
      materialParams.sdf.common.useShadows = true;
      materialParams.sdf.common.useAO = true;
      materialParams.sdf.common.depth = 0.3;
      materialParams.sdf.patterns[0].enabled = true;
      materialParams.sdf.patterns[0].xDistance = 5.2;
      materialParams.sdf.patterns[0].yDistance = 3.2;
      materialParams.sdf.patterns[1].enabled = true;
      materialParams.sdf.patterns[1].xDistance = 5.2;
      materialParams.sdf.patterns[1].yDistance = 3.2;
      materialParams.sdf.patterns[1].yOffset = 1.6;
      materialParams.sdf.patterns[1].xOffset = 2.6;
      const loadOptions = {
        ...defaultDesignMaterialLoadOptions,
        disableSDF: false,
        disableLayers: false,
        useWebgl2TextureAtlas: renderer.capabilities.isWebGL2,
      };
      const material = await designMaterialNew(
        materialParams,
        renderer,
        (url, _overrideFormat) => {
          assertIsNotNull(fetchedTexturesRef.current);
          return [fetchedTexturesRef.current[url], Promise.resolve()];
        },
        true,
        undefined,
        loadOptions,
      );
      const meshes = getAllMeshes(scene);
      for (const mesh of meshes) {
        mesh.material = material;
      }
    },
  },
  {
    name: "Material #3",
    enable: async (
      renderer: THREE.WebGLRenderer,
      scene: SSFXRendererScene,
      _renderParams: OnScreenRendererParamOverrides,
      fetchedTexturesRef: MutableRefObject<{ [name: string]: THREE.Texture } | null>,
    ) => {
      const materialParams = cloneDefaultMaterialParams();
      materialParams.color = "#55ff33";
      materialParams.layers[0].enabled = true;
      materialParams.layers[0].blendMode = "multiply";
      materialParams.layers[0].useForHeight = true;
      materialParams.layers[0].heightInfluence = 0.2;
      materialParams.sdf.common.maskOnly = false;
      materialParams.sdf.common.useShadows = true;
      materialParams.sdf.common.useAO = true;
      materialParams.sdf.common.depth = 0.3;
      materialParams.sdf.patterns[0].enabled = true;
      materialParams.sdf.patterns[0].xDistance = 5.2;
      materialParams.sdf.patterns[0].yDistance = 3.2;
      materialParams.sdf.patterns[1].enabled = true;
      materialParams.sdf.patterns[1].xDistance = 5.2;
      materialParams.sdf.patterns[1].yDistance = 3.2;
      materialParams.sdf.patterns[1].yOffset = 1.6;
      materialParams.sdf.patterns[1].xOffset = 2.6;
      const loadOptions = {
        ...defaultDesignMaterialLoadOptions,
        disableSDF: false,
        disableLayers: false,
        useWebgl2TextureAtlas: renderer.capabilities.isWebGL2,
      };
      const material = await designMaterialNew(
        materialParams,
        renderer,
        (url, _overrideFormat) => {
          assertIsNotNull(fetchedTexturesRef.current);
          return [fetchedTexturesRef.current[url], Promise.resolve()];
        },
        true,
        undefined,
        loadOptions,
      );
      const meshes = getAllMeshes(scene);
      for (const mesh of meshes) {
        mesh.material = material;
      }
    },
  },
];

function GraphicsTroubleshootingTestPage(): JSX.Element {
  const [rendererInfo, setRendererInfo] = useState<string>("");
  const [webglVersionInfo, setWebglVersionInfo] = useState<string>("");
  const [featureIndex, setFeatureIndex] = useState<number>(0);
  const [log, setLog] = useState<string>("");
  const [sectionKeys, setSectionKeys] = useState<GraphicsTroubleshootingSectionKey[]>([]);
  const [readyToRender, setReadyToRender] = useState<boolean>(false);
  const [showTestSection, setShowTestSection] = useState<boolean>(false);

  const canvasParentRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const renderTargetRef = useRef<SSFXRenderTarget | undefined>(undefined);
  const renderTargetPoolRef = useRef<RenderTargetPool | undefined>(undefined);
  const ssfxParamsManagerRef = useRef<SSFXRendererParamsManager | undefined>(undefined);
  const rendererRef = useRef<THREE.WebGLRenderer | undefined>(undefined);
  const ssfxSceneRef = useRef<SSFXRendererScene | undefined>(undefined);
  const testParamsRef = useRef<OnScreenRendererParamOverrides>({ id: 0, source: RenderParamsSource.TESTING });
  const fetchedTexturesRef = useRef<{ [name: string]: THREE.Texture } | null>(null);

  useEffect(() => {
    const contextInfo = initWebGLContext({
      framebufferOptions: {
        antialias: false,
        depthStencil: false,
      },
      allowWebGL1: true,
      throwOnGLError: false,
      logGLResourceUsage: false,
      label: "Testing",
    });
    if (contextInfo.hasWebGL2) {
      setWebglVersionInfo("WebGL 2.0");
    } else {
      setWebglVersionInfo("WebGL 1.0");
    }
    const ext = contextInfo.glContext.getExtension("WEBGL_debug_renderer_info");
    let unmaskedRenderer;
    if (!ext) {
      setRendererInfo("Active GPU Renderer: WEBGL_debug_renderer_info not supported");
    } else {
      unmaskedRenderer = contextInfo.glContext.getParameter(ext.UNMASKED_RENDERER_WEBGL);
      setRendererInfo("Active GPU Renderer: " + unmaskedRenderer);
    }

    // Reset state on hot reload
    setFeatureIndex(0);
    setLog("");

    if (!contextInfo.hasWebGL2) {
      setReadyToRender(false);
      return; // Not supported
    }

    if (renderTargetRef.current && canvasRef.current) {
      // Reset state on hot reload
      renderTargetPoolRef.current?.dispose();
      renderTargetRef.current.dispose();
      canvasParentRef.current?.removeChild(canvasRef.current);
    }
    canvasRef.current = contextInfo.canvas;
    canvasRef.current.width = 500;
    canvasRef.current.height = 500;
    const renderer = initTHREEJSRenderer(contextInfo.canvas, contextInfo.glContext, contextInfo.glContextAttributes);
    const capabilities = new WebGLExtendedCapabilities({ renderer });
    const { ssfxParamsManager, screenRenderTarget, renderTargetPool } = initPhysicallyBasedRenderer({
      renderer,
      capabilities,
      userOptions: {
        ssfxRenderer: true,
        useMSAA: false,
      },
    });
    renderTargetRef.current = screenRenderTarget;
    ssfxParamsManagerRef.current = ssfxParamsManager;
    renderTargetPoolRef.current = renderTargetPool;
    rendererRef.current = renderer;
    const testParams = {
      renderPipelineParams: {
        output: SSFXRendererOutput.LINEWORK,
      },
      screenRenderParams: {
        ssaaEnabled: false,
        reprojectionTAAEnabled: false,
      },
      id: 0,
      source: RenderParamsSource.TESTING,
    };
    assertIsNotUndefined(screenRenderTarget);
    assertIsNotUndefined(ssfxParamsManager);
    ssfxParamsManager.setRenderParameters(testParams);
    testParamsRef.current = testParams;
    screenRenderTarget.setFinalRenderSize(canvasRef.current.width, canvasRef.current.height);
    assertIsNotUndefined(canvasParentRef.current);
    canvasParentRef.current?.appendChild(canvasRef.current);

    const threeScene = new THREE.Scene();
    threeScene.background = new THREE.Color(0xffffff);
    const scene = new SSFXRendererScene({ scene: threeScene });
    ssfxSceneRef.current = scene;

    screenRenderTarget.setScene(ssfxSceneRef.current);
    screenRenderTarget.setCamera(camera);

    setReadyToRender(true);
  }, []);

  async function enableFeature() {
    if (featureIndex >= features.length) {
      return;
    }
    const renderTarget = renderTargetRef.current;
    if (!renderTarget) {
      return;
    }
    const ssfxParamsManager = ssfxParamsManagerRef.current;
    if (!ssfxParamsManager) {
      return;
    }
    const renderer = rendererRef.current;
    if (!renderer) {
      return;
    }
    const scene = ssfxSceneRef.current;
    if (!scene) {
      return;
    }
    const testParams = testParamsRef.current;
    await features[featureIndex].enable(renderer, scene, testParams, fetchedTexturesRef);
    ++testParams.id;
    const iterations = testParams.screenRenderParams?.ssaaEnabled ? 4 : 1;
    for (let i = 0; i < iterations; ++i) {
      ssfxParamsManager.setupParamsForRender(scene, camera);
      if (i === 0) {
        renderTarget.beginFrame(ssfxParamsManager.screenRenderParams);
        scene.updateShadowMap(renderer, camera);
      }
      renderTarget.applyRenderParams(ssfxParamsManager.getRenderParams());
      renderTarget.accumulateSampleIfNeeded();
    }
    renderTarget.renderSSAAOutput(null);
    setLog(log + "\nEnabled feature: " + features[featureIndex].name);
    setFeatureIndex(featureIndex + 1);
  }

  const featuresRemaining = featureIndex < features.length;

  const os = getOS();
  const deviceDriverSection =
    os === OS.WINDOWS
      ? GraphicsTroubleshootingSectionKey.WINDOWS_DEVICE_DRIVERS
      : GraphicsTroubleshootingSectionKey.GENERIC_DEVICE_DRIVERS;

  return (
    <>
      <Head>
        <title>{getPageTitle("Graphics Troubleshooting")}</title>
      </Head>
      <style>
        {`
        body {
          background-color: #e0e0e0;
        }
        .glInfo, .testParent, .docsParent {
          margin: 20px auto;
          max-width: 50em;
        }
        .highlight {
          font-weight: bold;
          background-color: transparent;
        }
        section {
          padding: 20px;
          background-color: #ffffff;
          border-radius: 10px;
        }
        section.docsSection {
          margin: 60px 0;
        }
        section h2 {
          margin-top: 0;
        }
        .illustration {
          max-width: 100%;
        }
        .warning {
          padding: 10px;
          background: #ffeeaa;
          border: 1px solid #ffaaaa;
        }
        .inline-header {
          font-weight: bold;
        }
        .showDocs {
          display: block;
          margin: 20px 0;
        }
        .hideDocsButton {
          margin: 20px 0;
        }
        `}
      </style>
      <div className="glInfo">
        <section>
          <h2>Graphics Troubleshooting</h2>
          <p>
            If you are experiencing crashes or very slow performance of the 3D viewer, please check the documentation
            for possible solutions.
          </p>

          <h3>System information</h3>
          <div className="infoString">{webglVersionInfo}</div>
          <div className="infoString">
            <TextWithHighlights
              text={rendererInfo}
              highlightedStrings={["AMD", "Intel", "NVIDIA"]}
              highlightClassName="highlight"
            />
          </div>
        </section>
        {webglVersionInfo.includes("1.0") ? (
          <p className="warning">
            ⚠️ It appears that your device does not support WebGL 2.0. Most graphics features are not available.{" "}
            <a href="#" onClick={() => setSectionKeys([deviceDriverSection])}>
              Make sure that the latest device drivers are installed
            </a>
            . If these steps don&apos;t help, consider switching to a newer device.
          </p>
        ) : (
          ""
        )}
        {rendererInfo.toLowerCase().includes("intel") && os === OS.WINDOWS ? (
          <div>
            <p className="warning">
              ⚠️ It appears that an Intel GPU is in use. If you are using a laptop computer and it also has an NVIDIA
              GPU, please{" "}
              <a href="#" onClick={() => setSectionKeys([GraphicsTroubleshootingSectionKey.WINDOWS_DUAL_GPU])}>
                check how to switch between GPUs
              </a>
              .
            </p>
            <p className="warning">
              ⚠️ It appears that an Intel GPU is in use. Intel driver version 31.0.101.4502 is known to cause some
              issues, so{" "}
              <a href="#" onClick={() => setSectionKeys([GraphicsTroubleshootingSectionKey.WINDOWS_DEVICE_DRIVERS])}>
                make sure you have up-to-date drivers
              </a>
              .
            </p>
          </div>
        ) : (
          ""
        )}
        {rendererInfo.toLowerCase().includes("swiftshader") ? (
          <p className="warning">
            ⚠️ It appears that GPU hardware acceleration is disabled. Expect lowered performance.{" "}
            <a href="#" onClick={() => setSectionKeys([GraphicsTroubleshootingSectionKey.HARDWARE_ACCELERATION])}>
              Make sure that you have not manually disabled hardware acceleration
            </a>
            , and that the{" "}
            <a href="#" onClick={() => setSectionKeys([deviceDriverSection])}>
              latest device drivers are installed
            </a>
            . If these steps don&apos;t help, consider switching to a newer device.
          </p>
        ) : (
          ""
        )}
      </div>
      <div className="docsParent">
        <GraphicsTroubleshootingDocs sectionKeys={sectionKeys}></GraphicsTroubleshootingDocs>
        {sectionKeys.length <= 1 ? (
          <button
            className="showDocs"
            onClick={() =>
              setSectionKeys([
                GraphicsTroubleshootingSectionKey.WINDOWS_DUAL_GPU,
                GraphicsTroubleshootingSectionKey.WINDOWS_DEVICE_DRIVERS,
                GraphicsTroubleshootingSectionKey.HARDWARE_ACCELERATION,
                GraphicsTroubleshootingSectionKey.REPORTING_DIAGNOSTICS,
              ])
            }
          >
            Show all documentation for Windows
          </button>
        ) : (
          ""
        )}
        {sectionKeys.length <= 1 ? (
          <button
            className="showDocs"
            onClick={() =>
              setSectionKeys([
                GraphicsTroubleshootingSectionKey.GENERIC_DEVICE_DRIVERS,
                GraphicsTroubleshootingSectionKey.HARDWARE_ACCELERATION,
                GraphicsTroubleshootingSectionKey.REPORTING_DIAGNOSTICS,
              ])
            }
          >
            Show all documentation for Apple/Linux
          </button>
        ) : (
          ""
        )}
        {sectionKeys.length > 0 ? (
          <button className="hideDocsButton" onClick={() => setSectionKeys([])}>
            Hide documentation
          </button>
        ) : (
          ""
        )}
      </div>
      <div className="testParent">
        {readyToRender && !showTestSection ? (
          <button onClick={() => setShowTestSection(true)}>Manual troubleshooting test</button>
        ) : (
          ""
        )}
        {readyToRender && showTestSection ? (
          <section>
            <h2>Manual troubleshooting test</h2>
            <p>
              If issues persist, you can try the this test to narrow down the cause and report the results to Higharc.
              You can enable graphics features one by one by pressing the button below. A video recording of the test
              can be useful for diagnosing issues. In case of a crash, please also{" "}
              <a href="#" onClick={() => setSectionKeys([GraphicsTroubleshootingSectionKey.REPORTING_DIAGNOSTICS])}>
                check instructions on how to send additional diagnostic information to Higharc
              </a>
              .
            </p>
            <div>
              <div>
                <button disabled={!featuresRemaining} onClick={enableFeature}>
                  {featuresRemaining
                    ? `Enable ${features[featureIndex].name} (${featureIndex} / ${features.length} steps completed)`
                    : "All features enabled"}
                </button>
              </div>
              <pre>{log}</pre>
            </div>
          </section>
        ) : (
          ""
        )}
        {/* Note that this is outside the test section because it needs to be loaded to set the readyToRender state */}
        <div ref={canvasParentRef}></div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/graphics-troubleshooting-test")({
  component: GraphicsTroubleshootingTestPage,
});
